/**
 * @fileoverview Active-sessions panel.
 * @description Lists the caller's own live sessions (device name, last-active time, and a
 * badge on whichever one is the current request) with a per-row revoke action, plus a single
 * "Log out of all sessions" action that ends every session including this one. A revoke's pending
 * state is read off the single shared mutation's own `variables` rather than per-row local state,
 * since only one revoke can plausibly be in flight from this panel at a time.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useTranslation } from 'react-i18next'

import { useLogoutAllSessions } from '@/features/auth/hooks/useLogoutAllSessions'
import { useRevokeSession } from '@/features/auth/hooks/useRevokeSession'
import { useSessions } from '@/features/auth/hooks/useSessions'
import Button from '@/shared/components/atoms/Button'
import LinkButton from '@/shared/components/atoms/LinkButton'

import type { SessionSummary } from '@/features/auth/services/session.service'

interface SessionRowProps {
  session: SessionSummary
  isRevoking: boolean
  onRevoke: () => void
}

/**
 * @description Renders one session's device details plus its revoke action (hidden for the
 * current session, since a caller can't revoke the request they're making the revoke with).
 * @param {SessionRowProps} props - The session and its row-local revoke state/handler.
 * @param {SessionSummary} props.session - The session to render.
 * @param {boolean} props.isRevoking - Whether this row's revoke request is in flight.
 * @param {() => void} props.onRevoke - Revokes this session.
 */
function SessionRow({ session, isRevoking, onRevoke }: SessionRowProps) {
  const { t } = useTranslation()
  const deviceLabel = session.deviceName ?? t('settings.sessions.unknownDevice')

  return (
    <li className="flex items-center justify-between gap-4 border-b border-divider py-3 last:border-b-0">
      <div>
        <p className="text-[14px] font-medium text-ink">
          {deviceLabel}
          {session.isCurrent && (
            <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">
              {t('settings.sessions.currentBadge')}
            </span>
          )}
        </p>
        <p className="text-[13px] text-slate">
          {t('settings.sessions.lastActivePrefix')}{' '}
          {new Date(session.lastActivityAt).toLocaleString()}
        </p>
      </div>
      {!session.isCurrent && (
        <LinkButton size="sm" onClick={onRevoke} disabled={isRevoking}>
          {isRevoking ? t('settings.sessions.revoking') : t('settings.sessions.revoke')}
        </LinkButton>
      )}
    </li>
  )
}

/**
 * @component
 */
function ActiveSessionsPanel() {
  const { t } = useTranslation()
  const sessionsQuery = useSessions()
  const revokeMutation = useRevokeSession()
  const logoutAllMutation = useLogoutAllSessions()

  return (
    <div className="max-w-sm">
      <div className="flex flex-col gap-3">
        <h3 className="text-[16px] font-semibold text-ink">{t('settings.sessions.title')}</h3>
        <Button
          variant="secondary"
          size="md"
          onClick={() => logoutAllMutation.mutate()}
          isLoading={logoutAllMutation.isPending}
        >
          {logoutAllMutation.isPending
            ? t('settings.sessions.loggingOutAll')
            : t('settings.sessions.logoutAll')}
        </Button>
      </div>

      {sessionsQuery.data && sessionsQuery.data.length === 0 && (
        <p className="mt-3 text-[14px] text-slate">{t('settings.sessions.empty')}</p>
      )}

      {sessionsQuery.data && sessionsQuery.data.length > 0 && (
        <ul className="mt-3">
          {sessionsQuery.data.map((session) => (
            <SessionRow
              key={session.sessionId}
              session={session}
              isRevoking={
                revokeMutation.isPending && revokeMutation.variables === session.sessionId
              }
              onRevoke={() => revokeMutation.mutate(session.sessionId)}
            />
          ))}
        </ul>
      )}
    </div>
  )
}

export default ActiveSessionsPanel
